/**
 * Authentication-related interfaces and enums
 */

export interface UserProfile {
  sub: string;
  name?: string;
  email?: string;
  given_name?: string;
  family_name?: string;
  preferred_username?: string;
}

export interface AuthState {
  isAuthenticated: boolean;
  isLoading: boolean;
  user: UserProfile | null;
  error: string | null;
}

/**
 * OAuth event types from angular-oauth2-oidc
 */
export enum OAuthEventType {
  TOKEN_RECEIVED = 'token_received',
  TOKEN_EXPIRES = 'token_expires',
  TOKEN_ERROR = 'token_error',
  TOKEN_REVOKE_ERROR = 'token_revoke_error',
  LOGOUT = 'logout',
  SESSION_CHANGED = 'session_changed',
  SESSION_ERROR = 'session_error',
  SESSION_TERMINATED = 'session_terminated',
  TOKEN_REFRESH_ERROR = 'token_refresh_error',
  CODE_ERROR = 'code_error',
  DISCOVERY_DOCUMENT_LOADED = 'discovery_document_loaded',
  DISCOVERY_DOCUMENT_LOAD_ERROR = 'discovery_document_load_error',
  JWKS_LOAD_ERROR = 'jwks_load_error',
  INVALID_NONCE_IN_STATE = 'invalid_nonce_in_state',
  USER_PROFILE_LOADED = 'user_profile_loaded',
  USER_PROFILE_LOAD_ERROR = 'user_profile_load_error',
}

/**
 * Storage keys used for authentication
 */
export enum AuthStorageKey {
  ACCESS_TOKEN = 'access_token',
  ID_TOKEN = 'id_token',
  REFRESH_TOKEN = 'refresh_token',
  EXPIRES_AT = 'expires_at',
  ID_TOKEN_EXPIRES_AT = 'id_token_expires_at',
  ID_TOKEN_STORED_AT = 'id_token_stored_at',
  ACCESS_TOKEN_STORED_AT = 'access_token_stored_at',
  GRANTED_SCOPES = 'granted_scopes',
  SESSION_STATE = 'session_state',
  PKCE_VERIFIER = 'PKCE_verifier',
  RETURN_URL = 'auth_return_url',
}

/**
 * HTTP status codes for authentication errors
 */
export enum AuthHttpStatus {
  UNAUTHORIZED = 401,
  FORBIDDEN = 403,
}

/**
 * Error types that can occur during authentication
 */
export enum AuthErrorType {
  TOKEN_EXPIRED = 'token_expired',
  TOKEN_INVALID = 'token_invalid',
  REFRESH_FAILED = 'refresh_failed',
  INITIALIZATION_FAILED = 'initialization_failed',
  NETWORK_ERROR = 'network_error',
  DISCOVERY_FAILED = 'discovery_failed',
  VALIDATION_FAILED = 'validation_failed',
}

/**
 * Auth error interface for structured error handling
 */
export interface AuthError {
  type: AuthErrorType;
  message: string;
  originalError?: unknown;
  timestamp: Date;
}

/**
 * Configuration for token refresh behavior
 */
export interface TokenRefreshConfig {
  enableAutomaticRefresh: boolean;
  refreshBeforeExpiry: number; // seconds
  maxRetryAttempts: number;
  retryDelay: number; // milliseconds
}

/**
 * Login options interface
 */
export interface LoginOptions {
  returnUrl?: string;
  additionalParams?: Record<string, string>;
  skipDiscoveryDocumentLoad?: boolean;
}

/**
 * Logout options interface
 */
export interface LogoutOptions {
  returnUrl?: string;
  revokeTokens?: boolean;
  clearStorage?: boolean;
}
